/**
 * Import Wizard State Machine
 *
 * XState machine that manages the Import Wizard lifecycle, including:
 * - Data fetching (via invoked services)
 * - Process polling (via invoked services)
 * - Step navigation
 * - Modal states
 *
 * Key principle: The machine controls all async operations directly.
 * No external useEffects needed for state synchronization.
 *
 * State Chart:
 *
 *                                     ┌─────────────────┐
 *                                     │  initializing   │
 *                                     │  invoke:        │
 *                                     │  fetchInitialData│
 *                                     └────────┬────────┘
 *                                              │
 *                        ┌─────────────────────┼─────────────────────┐
 *                        │ (status=running)    │ (status=idle)       │
 *                        ▼                     │                     │
 *               ┌─────────────────┐            │                     │
 *               │waitingForProcess│            │                     │
 *               │  invoke:        │            │                     │
 *               │  pollForIdle    │            │                     │
 *               └────────┬────────┘            │                     │
 *                        │ POLL_COMPLETE       │                     │
 *                        ▼                     │                     │
 *               ┌─────────────────┐            │                     │
 *               │   refreshing    │◄───────────┘                     │
 *               │  invoke:        │                                  │
 *               │  refetchData    │                                  │
 *               └────────┬────────┘                                  │
 *                        │                                           │
 *                        └───────────────┬───────────────────────────┘
 *                                        │
 *                                        ▼
 *                               ┌─────────────────┐
 *                               │    routing      │ (transient)
 *                               └────────┬────────┘
 *                                        │
 *           ┌────────────────────────────┼────────────────────────────┐
 *           │                            │                            │
 *           ▼                            ▼                            ▼
 * ┌───────────────────┐      ┌─────────────────┐      ┌─────────────────┐
 * │      step1        │      │     step2       │      │      step3      │
 * │ ┌───────────────┐ │      │                 │      │ ┌─────────────┐ │
 * │ │    confirm    │ │◄BACK─│                 │◄BACK─│ │   decide    │ │
 * │ └───────┬───────┘ │      │                 │      │ └──────┬──────┘ │
 * │         │         │      └────────┬────────┘      │        │        │
 * │         │         │               │               │        │        │
 * │         ▼         │               │               │        ▼        │
 * │ ┌───────────────┐ │               │               │ ┌─────────────┐ │
 * │ │ deselectModal │ │               │               │ │confirmModal │ │
 * │ └───────────────┘ │               │               │ └─────────────┘ │
 * └───────────────────┘               │               └────────┬────────┘
 *                                     ▼                        │
 *                            ┌─────────────────┐               │
 *                            │     step4       │◄──────────────┘
 *                            └────────┬────────┘    (after processing)
 *                                     │
 *                                     │ START_PROCESS
 *                                     ▼
 *                            ┌─────────────────────────┐
 *                            │      processing         │
 *                            │  ┌─────────────────┐    │
 *                            │  │    starting     │    │
 *                            │  │ invoke:         │    │
 *                            │  │ startProcess    │    │
 *                            │  └────────┬────────┘    │
 *                            │           │ onDone      │
 *                            │           ▼             │
 *                            │  ┌─────────────────┐    │
 *                            │  │    polling      │    │
 *                            │  │ invoke:         │    │
 *                            │  │ pollForIdle     │    │
 *                            │  └────────┬────────┘    │
 *                            └───────────┼─────────────┘
 *                                        │ POLL_COMPLETE
 *                                        ▼
 *                                    refreshing (then routing)
 */

import { setup, assign } from 'xstate'
import {
  fetchInitialDataActor,
  pollForIdleActor,
  refetchDataActor,
  saveAndReloadDataActor,
  startProcessActor,
  validateDealsActor,
} from './services'
import {
  WizardContext,
  WizardEvent,
  WizardStep,
  InternalState,
  NestedState,
  EventType,
  WizardMachineInput,
  MACHINE_ID,
  ProcessType,
  mapProcessRunning,
  computeCompanyData,
  computeAvailableSteps,
} from './types'
import { InternalColumns } from '../importer-types'

/**
 * Import Wizard State Machine
 */
export const wizardMachine = setup({
  types: {
    context: {} as WizardContext,
    events: {} as WizardEvent,
    input: {} as WizardMachineInput,
  },
  actors: {
    fetchInitialData: fetchInitialDataActor,
    pollForIdle: pollForIdleActor,
    refetchData: refetchDataActor,
    saveAndReloadData: saveAndReloadDataActor,
    startProcess: startProcessActor,
    validateDeals: validateDealsActor,
  },
  guards: {
    isProcessRunning: (_, params: { status: string }) =>
      params.status === 'running',
    hasStep1: ({ context }) =>
      context.availableSteps.includes(WizardStep.STEP1_CONFIRM),
    hasStep2: ({ context }) =>
      context.availableSteps.includes(WizardStep.STEP2_REVIEW),
    hasStep3: ({ context }) =>
      context.availableSteps.includes(WizardStep.STEP3_DECIDE),
    canGoBackToStep1: ({ context }) =>
      context.availableSteps.includes(WizardStep.STEP1_CONFIRM),
    canGoBackToStep2: ({ context }) =>
      context.availableSteps.includes(WizardStep.STEP2_REVIEW),
    canGoBackToStep3: ({ context }) =>
      context.availableSteps.includes(WizardStep.STEP3_DECIDE),
    // Guards for returning to origin step after save/process
    returnToStep1: ({ context }) =>
      context.returnToStep === WizardStep.STEP1_CONFIRM,
    returnToStep2: ({ context }) =>
      context.returnToStep === WizardStep.STEP2_REVIEW,
    returnToStep3: ({ context }) =>
      context.returnToStep === WizardStep.STEP3_DECIDE,
    returnToStep4: ({ context }) =>
      context.returnToStep === WizardStep.STEP4_IMPORT,
    hasReturnToStep: ({ context }) => context.returnToStep !== null,
  },
  actions: {
    setDataAndComputeSteps: assign(({ context, event }) => {
      // Extract data from event (works for both fetch and refetch)
      const eventData = (
        event as { output?: { data: unknown; skipMatching?: boolean } }
      ).output
      const data = eventData?.data as WizardContext['importData']
      const skipMatching = eventData?.skipMatching ?? context.skipMatching

      const companyData = computeCompanyData(data)
      const availableSteps = computeAvailableSteps(companyData, skipMatching)

      return {
        importData: data,
        skipMatching,
        availableSteps,
        companiesPrematched: companyData.companiesPrematched,
        companiesPendingReview: companyData.companiesPendingReview,
        companiesLeftUnmatched: companyData.companiesLeftUnmatched,
        allCompanies: companyData.allCompanies,
      }
    }),
    setInitialData: assign(({ event }) => {
      // The event comes from invoke onDone, so we need to extract output carefully
      const eventWithOutput = event as unknown as {
        output: {
          data: unknown
          skipMatching: boolean
          processRunning: string | null
          importName: string
        }
      }
      const eventData = eventWithOutput.output
      return {
        importData: eventData.data as WizardContext['importData'],
        skipMatching: eventData.skipMatching,
        currentProcess: mapProcessRunning(eventData.processRunning),
        importName: eventData.importName,
      }
    }),
    incrementDataVersion: assign({
      dataVersion: ({ context }) => context.dataVersion + 1,
    }),
    setCurrentProcess: assign(({ event }) => {
      if (event.type === EventType.START_PROCESS) {
        return {
          currentProcess: event.processType,
          processData: event.data ?? null,
        }
      }
      return { currentProcess: null, processData: null }
    }),
    clearCurrentProcess: assign({
      currentProcess: null,
      processData: null,
    }),
    setError: assign({
      error: ({ event }) => {
        if (event.type === EventType.POLL_ERROR) {
          return event.error
        }
        const err = (event as { error?: Error | string }).error
        if (err instanceof Error) return err.message
        if (typeof err === 'string') return err
        return 'An unexpected error occurred'
      },
    }),
    clearError: assign({
      error: null,
    }),
    setReturnToStep: assign({
      returnToStep: ({ event }) => {
        if (event.type === EventType.SAVE_AND_RELOAD) {
          return event.originStep
        }
        if (event.type === EventType.START_PROCESS && event.originStep) {
          return event.originStep
        }
        return null
      },
    }),
    clearReturnToStep: assign({
      returnToStep: null,
    }),
    setReturnToStep4: assign({
      returnToStep: () => WizardStep.STEP4_IMPORT,
    }),
    // Deal validation actions
    setDealValidationData: assign({
      dealValidationData: ({ event }) => {
        // This action is only called from onDone of validateDeals actor
        const output = (
          event as { output?: WizardContext['dealValidationData'] }
        ).output
        return output ?? null
      },
    }),
    clearDealValidationData: assign({
      dealValidationData: null,
      dealProductAddedByModal: false,
    }),
    // Revert deal product changes when canceling from product modal
    // Only removes if dealProductAddedByModal is true (we added it, not from CSV)
    revertDealProduct: assign({
      importData: ({ context }) => {
        if (!context.importData || !context.dealProductAddedByModal) {
          return context.importData
        }

        // Remove deal_product from all rows
        const updatedRows = context.importData.rows.map((row) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [InternalColumns.DEAL_PRODUCT]: _removed, ...rest } = row
          return rest
        })

        // Remove the deal_product header if it was added by applyDealProduct
        const updatedHeaders = context.importData.headers.filter(
          (h) =>
            h.original !== InternalColumns.DEAL_PRODUCT &&
            h.mapped !== 'deal_product'
        )

        return {
          ...context.importData,
          rows: updatedRows,
          headers: updatedHeaders,
        }
      },
      dealProductAddedByModal: false,
    }),
    applyValidationFixes: assign({
      importData: ({ context, event }) => {
        if (event.type !== EventType.DEALS_VALIDATION_FIXED) {
          return context.importData
        }
        if (!context.importData) return null

        const { changes, columnName } = event

        // Rows are keyed by ORIGINAL CSV column names (e.g., "Owner Email"),
        // not mapped names (e.g., "deal_account_owner"). Look up the original key.
        const header = context.importData.headers.find(
          (h) => h.mapped === columnName
        )
        if (!header) {
          throw new Error(
            `Header not found for column: ${columnName}. ` +
              `Cannot apply validation fixes - the column may have been removed.`
          )
        }
        const originalKey = header.original

        // O(1) lookup map: original value → new mapped value
        const changeMap = new Map<string, string>()
        for (const change of changes) {
          if (change.mapped) {
            changeMap.set(change.original, change.mapped)
          }
        }

        const updatedRows = context.importData.rows.map((row) => {
          const currentValue = row[originalKey]
          if (typeof currentValue === 'string' && changeMap.has(currentValue)) {
            return { ...row, [originalKey]: changeMap.get(currentValue) }
          }
          return row
        })

        return { ...context.importData, rows: updatedRows }
      },
    }),
    // Update setCurrentProcess to handle validation success
    setDealsProcessAfterValidation: assign({
      currentProcess: () => ProcessType.SETTING_DEALS,
      processData: ({ context }) => context.importData,
    }),
    // Apply deal product to all rows and add header (if not already present)
    // Sets dealProductAddedByModal flag so revert knows what to clean up
    applyDealProduct: assign({
      importData: ({ context, event }) => {
        if (event.type !== EventType.DEAL_PRODUCT_SELECTED) {
          return context.importData
        }
        if (!context.importData) return null

        const { productName } = event

        // Add the deal_product to all rows
        const updatedRows = context.importData.rows.map((row) => ({
          ...row,
          [InternalColumns.DEAL_PRODUCT]: productName,
        }))

        // Add the deal_product header only if it doesn't already exist
        const headerExists = context.importData.headers.some(
          (h) =>
            h.original === InternalColumns.DEAL_PRODUCT ||
            h.mapped === 'deal_product'
        )

        const updatedHeaders = headerExists
          ? context.importData.headers
          : [
              ...context.importData.headers,
              {
                original: InternalColumns.DEAL_PRODUCT,
                mapped: 'deal_product',
                label: 'Deal Product',
              },
            ]

        return {
          ...context.importData,
          rows: updatedRows,
          headers: updatedHeaders,
        }
      },
      dealProductAddedByModal: true,
    }),
  },
}).createMachine({
  id: MACHINE_ID,
  initial: InternalState.INITIALIZING,
  context: ({ input }) => ({
    importNumber: input.importNumber,
    importName: '',
    importData: null,
    skipMatching: false,
    availableSteps: [],
    companiesPrematched: [],
    companiesPendingReview: [],
    companiesLeftUnmatched: [],
    allCompanies: [],
    dataVersion: 0,
    currentProcess: null,
    processData: null,
    error: null,
    returnToStep: null,
    dealValidationData: null,
    dealProductAddedByModal: false,
  }),

  states: {
    // ==================== INITIALIZING ====================
    // Fetch initial data. If process is running, go to waitingForProcess.
    // Otherwise, compute steps and route.
    [InternalState.INITIALIZING]: {
      invoke: {
        src: 'fetchInitialData',
        input: ({ context }) => ({ importNumber: context.importNumber }),
        onDone: [
          {
            // Process is running - go wait for it
            guard: {
              type: 'isProcessRunning',
              params: ({ event }) => ({ status: event.output.status }),
            },
            target: InternalState.WAITING_FOR_PROCESS,
            actions: ['setInitialData'],
          },
          {
            // No process running - go to routing
            target: InternalState.ROUTING,
            actions: ['setDataAndComputeSteps', 'incrementDataVersion'],
          },
        ],
        onError: {
          target: InternalState.ERROR,
          actions: ['setError'],
        },
      },
    },

    // ==================== WAITING FOR PROCESS ====================
    // Poll until backend process completes, then refetch data.
    [InternalState.WAITING_FOR_PROCESS]: {
      invoke: {
        src: 'pollForIdle',
        input: ({ context }) => ({ importNumber: context.importNumber }),
      },
      on: {
        [EventType.POLL_COMPLETE]: InternalState.REFRESHING,
        [EventType.POLL_ERROR]: {
          target: InternalState.ERROR,
          actions: ['setError'],
        },
      },
    },

    // ==================== REFRESHING ====================
    // Fetch fresh data after process completes.
    // If returnToStep is set (from processing state), return to that step.
    // Otherwise, use routing to determine the appropriate step.
    [InternalState.REFRESHING]: {
      invoke: {
        src: 'refetchData',
        input: ({ context }) => ({ importNumber: context.importNumber }),
        onDone: [
          // Check returnToStep first (for Step 4 imports that should return to Step 4)
          {
            target: WizardStep.STEP1_CONFIRM,
            guard: 'returnToStep1',
            actions: [
              'setDataAndComputeSteps',
              'incrementDataVersion',
              'clearCurrentProcess',
              'clearReturnToStep',
            ],
          },
          {
            target: WizardStep.STEP2_REVIEW,
            guard: 'returnToStep2',
            actions: [
              'setDataAndComputeSteps',
              'incrementDataVersion',
              'clearCurrentProcess',
              'clearReturnToStep',
            ],
          },
          {
            target: WizardStep.STEP3_DECIDE,
            guard: 'returnToStep3',
            actions: [
              'setDataAndComputeSteps',
              'incrementDataVersion',
              'clearCurrentProcess',
              'clearReturnToStep',
            ],
          },
          {
            target: WizardStep.STEP4_IMPORT,
            guard: 'returnToStep4',
            actions: [
              'setDataAndComputeSteps',
              'incrementDataVersion',
              'clearCurrentProcess',
              'clearReturnToStep',
            ],
          },
          // Default: use routing (for Step 3 confirm matches flow)
          {
            target: InternalState.ROUTING,
            actions: [
              'setDataAndComputeSteps',
              'incrementDataVersion',
              'clearCurrentProcess',
            ],
          },
        ],
        onError: {
          target: InternalState.ERROR,
          actions: ['setError'],
        },
      },
    },

    // ==================== ROUTING ====================
    // Transient state to determine which step to show based on available steps.
    [InternalState.ROUTING]: {
      always: [
        { target: WizardStep.STEP1_CONFIRM, guard: 'hasStep1' },
        { target: WizardStep.STEP2_REVIEW, guard: 'hasStep2' },
        { target: WizardStep.STEP3_DECIDE, guard: 'hasStep3' },
        { target: WizardStep.STEP4_IMPORT },
      ],
    },

    // ==================== STEP 1: CONFIRM MATCHES ====================
    [WizardStep.STEP1_CONFIRM]: {
      initial: NestedState.CONFIRM,
      on: {
        [EventType.SAVE_AND_RELOAD]: `#${MACHINE_ID}.${InternalState.SAVING}`,
      },
      states: {
        [NestedState.CONFIRM]: {
          on: {
            [EventType.GO_NEXT]: [
              {
                target: `#${MACHINE_ID}.${WizardStep.STEP2_REVIEW}`,
                guard: 'hasStep2',
              },
              { target: `#${MACHINE_ID}.${WizardStep.STEP3_DECIDE}` },
            ],
            [EventType.OPEN_DESELECT_MODAL]: NestedState.DESELECT_MODAL,
          },
        },
        [NestedState.DESELECT_MODAL]: {
          on: {
            [EventType.CONFIRM_DESELECT]: [
              {
                target: `#${MACHINE_ID}.${WizardStep.STEP2_REVIEW}`,
                guard: 'hasStep2',
              },
              { target: `#${MACHINE_ID}.${WizardStep.STEP3_DECIDE}` },
            ],
            [EventType.CANCEL_DESELECT]: NestedState.CONFIRM,
          },
        },
      },
    },

    // ==================== STEP 2: REVIEW PENDING ====================
    [WizardStep.STEP2_REVIEW]: {
      on: {
        [EventType.GO_NEXT]: WizardStep.STEP3_DECIDE,
        [EventType.GO_BACK]: [
          { target: WizardStep.STEP1_CONFIRM, guard: 'canGoBackToStep1' },
        ],
        [EventType.SAVE_AND_RELOAD]: InternalState.SAVING,
      },
    },

    // ==================== STEP 3: DECIDE UNMATCHED ====================
    [WizardStep.STEP3_DECIDE]: {
      initial: NestedState.DECIDE,
      on: {
        [EventType.SAVE_AND_RELOAD]: `#${MACHINE_ID}.${InternalState.SAVING}`,
      },
      states: {
        [NestedState.DECIDE]: {
          on: {
            [EventType.OPEN_CONFIRM_MODAL]: NestedState.CONFIRM_MODAL,
            [EventType.GO_BACK]: [
              {
                target: `#${MACHINE_ID}.${WizardStep.STEP2_REVIEW}`,
                guard: 'canGoBackToStep2',
              },
              {
                target: `#${MACHINE_ID}.${WizardStep.STEP1_CONFIRM}`,
                guard: 'canGoBackToStep1',
              },
            ],
            // Direct to processing for skipMatching flow
            [EventType.START_PROCESS]: {
              target: `#${MACHINE_ID}.${InternalState.PROCESSING}`,
              actions: ['setCurrentProcess'],
            },
          },
        },
        [NestedState.CONFIRM_MODAL]: {
          on: {
            [EventType.START_PROCESS]: {
              target: `#${MACHINE_ID}.${InternalState.PROCESSING}`,
              actions: ['setCurrentProcess'],
            },
            [EventType.CANCEL_CONFIRM]: NestedState.DECIDE,
          },
        },
      },
    },

    // ==================== STEP 4: IMPORT ====================
    [WizardStep.STEP4_IMPORT]: {
      initial: NestedState.IMPORT,
      on: {
        [EventType.SAVE_AND_RELOAD]: `#${MACHINE_ID}.${InternalState.SAVING}`,
      },
      states: {
        // Default: showing import options (tags, deals, contacts, notes)
        [NestedState.IMPORT]: {
          on: {
            [EventType.GO_BACK]: [
              {
                target: `#${MACHINE_ID}.${WizardStep.STEP3_DECIDE}`,
                guard: 'canGoBackToStep3',
              },
            ],
            // Start validation flow instead of direct process
            [EventType.VALIDATE_DEALS]: NestedState.VALIDATING_DEALS,
            // Other import types go directly to processing
            [EventType.START_PROCESS]: {
              target: `#${MACHINE_ID}.${InternalState.PROCESSING}`,
              actions: ['setCurrentProcess'],
            },
          },
        },

        // Validating deals: async check deal_product, account owners, and sales stages
        [NestedState.VALIDATING_DEALS]: {
          invoke: {
            src: 'validateDeals',
            input: ({ context }) => ({
              importNumber: context.importNumber,
              importData: context.importData,
            }),
            onDone: [
              {
                // Product required - show product selection modal
                guard: ({ event }) => event.output?.type === 'product_required',
                target: NestedState.DEAL_PRODUCT_MODAL,
                actions: ['setDealValidationData'],
              },
              {
                // Mapping error - show validation modal for account_owner/sales_stage
                guard: ({ event }) => event.output?.type === 'mapping',
                target: NestedState.DEAL_VALIDATION_MODAL,
                actions: ['setDealValidationData'],
              },
              {
                // Validation passed (returned null) - proceed to processing
                // Note: setReturnToStep4 is needed because PROCESSING.entry runs setReturnToStep,
                // which only recognizes START_PROCESS/SAVE_AND_RELOAD events, not onDone from validateDeals
                target: `#${MACHINE_ID}.${InternalState.PROCESSING}`,
                actions: [
                  'clearDealValidationData',
                  'setDealsProcessAfterValidation',
                  'setReturnToStep4',
                ],
              },
            ],
            onError: {
              target: `#${MACHINE_ID}.${InternalState.ERROR}`,
              actions: ['setError'],
            },
          },
        },

        // Modal state: user selects deal product (when deal_product column is missing)
        [NestedState.DEAL_PRODUCT_MODAL]: {
          on: {
            // User selected a product - apply it, save to backend, then retry validation
            [EventType.DEAL_PRODUCT_SELECTED]: {
              target: NestedState.SAVING_VALIDATION_FIXES,
              actions: ['applyDealProduct', 'clearDealValidationData'],
            },
            // User cancelled - revert changes and return to import state
            [EventType.CANCEL_DEAL_VALIDATION]: {
              target: NestedState.IMPORT,
              actions: ['revertDealProduct', 'clearDealValidationData'],
            },
          },
        },

        // Modal state: user fixes validation errors (account_owner/sales_stage mapping)
        [NestedState.DEAL_VALIDATION_MODAL]: {
          on: {
            // User saved fixes - apply them, save to backend, then retry validation
            [EventType.DEALS_VALIDATION_FIXED]: {
              target: NestedState.SAVING_VALIDATION_FIXES,
              actions: ['applyValidationFixes'],
            },
            // User cancelled - return to import state (no data to revert for mapping fixes)
            [EventType.CANCEL_DEAL_VALIDATION]: {
              target: NestedState.IMPORT,
              actions: ['clearDealValidationData'],
            },
          },
        },

        // Saving validation fixes: save to backend before re-validating
        [NestedState.SAVING_VALIDATION_FIXES]: {
          invoke: {
            src: 'saveAndReloadData',
            input: ({ context }) => ({
              importNumber: context.importNumber,
              data: context.importData!,
              skipMatching: context.skipMatching,
            }),
            onDone: {
              target: NestedState.VALIDATING_DEALS,
              actions: ['setDataAndComputeSteps', 'incrementDataVersion'],
            },
            onError: {
              target: `#${MACHINE_ID}.${InternalState.ERROR}`,
              actions: ['setError'],
            },
          },
        },
      },
    },

    // ==================== PROCESSING ====================
    // User-initiated process. Save data + call API, then poll until complete, then refresh.
    // Compound state: starting (invoke API) → polling (poll for idle)
    [InternalState.PROCESSING]: {
      entry: 'setReturnToStep',
      initial: 'starting',
      states: {
        starting: {
          invoke: {
            src: 'startProcess',
            input: ({ context }) => ({
              importNumber: context.importNumber,
              processType: context.currentProcess,
              data: context.processData,
            }),
            onDone: 'polling',
            onError: {
              target: `#${MACHINE_ID}.${InternalState.ERROR}`,
              actions: ['setError'],
            },
          },
        },
        polling: {
          invoke: {
            src: 'pollForIdle',
            input: ({ context }) => ({ importNumber: context.importNumber }),
          },
          on: {
            [EventType.POLL_COMPLETE]: `#${MACHINE_ID}.${InternalState.REFRESHING}`,
            [EventType.POLL_ERROR]: {
              target: `#${MACHINE_ID}.${InternalState.ERROR}`,
              actions: ['setError'],
            },
          },
        },
      },
    },

    // ==================== SAVING ====================
    // Save current data and reload, returning to the origin step.
    // This bypasses routing to preserve the user's current step.
    [InternalState.SAVING]: {
      entry: 'setReturnToStep',
      invoke: {
        src: 'saveAndReloadData',
        input: ({ context, event }) => {
          const e = event as Extract<
            WizardEvent,
            { type: EventType.SAVE_AND_RELOAD }
          >
          return {
            importNumber: context.importNumber,
            data: e.data,
            skipMatching: e.skipMatching,
          }
        },
        onDone: [
          {
            target: WizardStep.STEP1_CONFIRM,
            guard: 'returnToStep1',
            actions: [
              'setDataAndComputeSteps',
              'incrementDataVersion',
              'clearReturnToStep',
            ],
          },
          {
            target: WizardStep.STEP2_REVIEW,
            guard: 'returnToStep2',
            actions: [
              'setDataAndComputeSteps',
              'incrementDataVersion',
              'clearReturnToStep',
            ],
          },
          {
            target: WizardStep.STEP3_DECIDE,
            guard: 'returnToStep3',
            actions: [
              'setDataAndComputeSteps',
              'incrementDataVersion',
              'clearReturnToStep',
            ],
          },
          {
            // Default fallback to step4
            target: WizardStep.STEP4_IMPORT,
            actions: [
              'setDataAndComputeSteps',
              'incrementDataVersion',
              'clearReturnToStep',
            ],
          },
        ],
        onError: [
          // On error, return to origin step
          // Note: We don't set context.error here because:
          // 1. baseAPICall already shows a toast for API errors
          // 2. We don't want to trigger the error state UI
          {
            target: WizardStep.STEP1_CONFIRM,
            guard: 'returnToStep1',
            actions: ['clearReturnToStep'],
          },
          {
            target: WizardStep.STEP2_REVIEW,
            guard: 'returnToStep2',
            actions: ['clearReturnToStep'],
          },
          {
            target: WizardStep.STEP3_DECIDE,
            guard: 'returnToStep3',
            actions: ['clearReturnToStep'],
          },
          {
            target: WizardStep.STEP4_IMPORT,
            actions: ['clearReturnToStep'],
          },
        ],
      },
    },

    // ==================== ERROR ====================
    [InternalState.ERROR]: {
      on: {
        [EventType.RETRY]: {
          target: InternalState.INITIALIZING,
          actions: ['clearError'],
        },
      },
    },
  },
})

export type WizardMachine = typeof wizardMachine
